import { Trans, useLingui } from '@lingui/react/macro';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { homeDecisionsQuery } from '../lib/agency-home-api.js';
import {
  approveBrandContentSlot,
  type FeedCard,
  fetchBrandContentSlot,
  rejectBrandContentSlot,
  requestChangesBrandContentSlot,
} from '../lib/api.js';
import { feedQuery } from '../lib/queries.js';
import { refreshQueries } from '../lib/refresh.js';
import { Drawer, DrawerCloseButton, useDrawer } from './drawer.js';

// 48/N — slot approval drawer. Resolves a slot_approval_pending card
// with one of three actions:
//
//   approve         → mag publishes at scheduled_for
//   reject          → slot stays rejected; founder can drop it
//   request changes → inline note field; the JIT picks the slot back
//                     up and redrafts on the next tick
//
// Chrome mirrors the existing FeedCardDrawer / SlotDrawer: right-side
// slide-in, scrim, ESC + close button, body scroll lock. Footer is
// right-aligned per the brand button rule.

interface SlotApprovalDrawerProps {
  card: FeedCard;
  brandId: string;
  onClose: () => void;
}

export function SlotApprovalDrawer({ card, brandId, onClose }: SlotApprovalDrawerProps) {
  const { t } = useLingui();
  const queryClient = useQueryClient();
  const router = useRouter();
  const { visible, requestClose } = useDrawer(onClose);
  const [mode, setMode] = useState<'view' | 'changes'>('view');
  const [note, setNote] = useState('');
  const payload =
    card.payload && 'kind' in card.payload && card.payload.kind === 'slot_approval_pending'
      ? card.payload
      : null;
  const slotId = payload?.slotId ?? null;

  // Fetch the slot's FULL state on open. The feed-card payload only
  // carries a truncated preview (captionPreview ≤ 280 chars, no image
  // brief, no rationale); this gives the founder the complete picture
  // before approving. The payload stays the instant-render fallback so
  // the drawer never flashes empty while this loads.
  const slotQuery = useQuery({
    queryKey: ['content-slot', slotId],
    queryFn: () => fetchBrandContentSlot(slotId as string),
    enabled: Boolean(slotId),
    staleTime: 0,
  });
  const draft = slotQuery.data?.draft ?? null;
  const format = slotQuery.data?.format ?? payload?.format ?? null;
  const isVideo = format === 'reel' || format === 'video';
  const mediaUrl = draft?.imageUrl ?? draft?.videoUrl ?? payload?.mediaUrl ?? null;
  // Full caption once loaded; truncated preview meanwhile.
  const caption = draft?.caption ?? payload?.captionPreview ?? '';
  const captionIsFull = Boolean(draft?.caption);
  const imageBrief = draft?.imageBrief ?? null;
  const rationale = slotQuery.data?.rationale ?? null;

  const refreshAfterResolve = async () => {
    await refreshQueries(
      queryClient,
      homeDecisionsQuery(brandId).queryKey,
      feedQuery(brandId).queryKey,
      ['brand-decision-count', brandId],
      ['brand-channel'],
    );
    // /home reads its feed cards from loader data (ensureQueryData in
    // beforeLoad), which doesn't subscribe — refetching the query alone
    // leaves the route showing the stale, just-approved card. Invalidate
    // so the loader re-runs against the fresh (now-dismissed) feed.
    await router.invalidate();
  };

  const approveMutation = useMutation({
    mutationFn: async () => {
      if (!slotId) throw new Error('missing slot id');
      await approveBrandContentSlot(slotId);
    },
    onSuccess: async () => {
      await refreshAfterResolve();
      toast.success("approved. i'll ship it at scheduled time.");
      requestClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const rejectMutation = useMutation({
    mutationFn: async () => {
      if (!slotId) throw new Error('missing slot id');
      await rejectBrandContentSlot(slotId);
    },
    onSuccess: async () => {
      await refreshAfterResolve();
      toast.success("rejected. i'll leave the slot blank.");
      requestClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });
  const requestChangesMutation = useMutation({
    mutationFn: async () => {
      if (!slotId) throw new Error('missing slot id');
      if (note.trim().length === 0) throw new Error('add a note so i know what to change');
      await requestChangesBrandContentSlot(slotId, note.trim());
    },
    onSuccess: async () => {
      await refreshAfterResolve();
      toast.success("got it. i'll redraft with your note.");
      requestClose();
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const pending =
    approveMutation.isPending || rejectMutation.isPending || requestChangesMutation.isPending;

  if (!payload) {
    return null;
  }

  return (
    <Drawer
      visible={visible}
      onRequestClose={requestClose}
      maxWidthClass="max-w-[640px]"
      ariaLabelledBy="slot-approval-drawer-title"
    >
      <header className="flex items-start justify-between gap-3 border-b border-[var(--line)] bg-white px-5 py-4">
        <div className="min-w-0">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--plum)]">
            {'↘'} <Trans>ready for your call</Trans>
          </p>
          <h2
            id="slot-approval-drawer-title"
            className="mt-1 font-display text-[20px] font-extrabold leading-tight tracking-[-0.02em]"
          >
            {payload.concept}
          </h2>
          <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
            <span>{payload.scheduledForLabel}</span>
            <span aria-hidden="true">·</span>
            <span>{payload.format}</span>
            <span aria-hidden="true">·</span>
            <span>{payload.pillar.toLowerCase()}</span>
          </p>
        </div>
        <DrawerCloseButton onClick={requestClose} />
      </header>

      <div className="flex-1 overflow-y-auto px-5 py-5">
        {mediaUrl ? (
          // Show the creative at its NATIVE aspect ratio — posts render
          // 4:5, carousels 1:1, reels/stories 9:16, etc. Forcing a
          // square here cropped the top + bottom off a 4:5 post. We cap
          // height so a tall 9:16 still leaves the caption + actions in
          // reach (the drawer body scrolls), and center it on a neutral
          // mat.
          <div className="mb-4 flex justify-center overflow-hidden rounded-xl border border-[var(--ink)]/15 bg-[var(--ink)]/5">
            {isVideo ? (
              <video
                src={mediaUrl}
                controls
                playsInline
                className="block h-auto max-h-[70vh] w-auto max-w-full bg-black"
              >
                <track kind="captions" />
              </video>
            ) : (
              <img
                src={mediaUrl}
                alt={payload.concept}
                className="block h-auto max-h-[70vh] w-auto max-w-full object-contain"
              />
            )}
          </div>
        ) : (
          <div className="mb-4 flex aspect-square w-full items-center justify-center rounded-xl border border-dashed border-[var(--ink)]/25 bg-[var(--paper)]">
            <p className="font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--ink)]/55">
              {slotQuery.isPending ? <Trans>↘ loading…</Trans> : <Trans>↘ no media yet</Trans>}
            </p>
          </div>
        )}

        <div className="mb-4 rounded-xl border border-[var(--ink)]/15 bg-white p-3.5">
          <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]">
            ↘ {captionIsFull ? <Trans>caption</Trans> : <Trans>caption preview</Trans>}
          </p>
          <p className="mt-1.5 whitespace-pre-line text-[13.5px] leading-[1.55] text-[var(--ink)]/90">
            {caption}
          </p>
        </div>

        {rationale ? (
          <div className="mb-4 rounded-xl border border-[var(--ink)]/15 bg-white p-3.5">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]">
              {'↘'} <Trans>why this post</Trans>
            </p>
            <p className="mt-1.5 whitespace-pre-line text-[13px] leading-[1.55] text-[var(--ink)]/80">
              {rationale}
            </p>
          </div>
        ) : null}

        {imageBrief ? (
          <div className="mb-4 rounded-xl border border-[var(--ink)]/15 bg-white p-3.5">
            <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]">
              {'↘'} <Trans>visual brief</Trans>
            </p>
            <p className="mt-1.5 whitespace-pre-line text-[13px] leading-[1.55] text-[var(--ink)]/80">
              {imageBrief}
            </p>
          </div>
        ) : null}

        {slotQuery.isError ? (
          <p className="mb-4 rounded-md border border-[var(--pulse)]/40 bg-[var(--pulse)]/5 px-3 py-2 text-[12px] leading-[1.45] text-[var(--pulse)]">
            {'↘'}{' '}
            <Trans>
              couldn't load the full draft — showing the preview. {String(slotQuery.error)}
            </Trans>
          </p>
        ) : null}

        {mode === 'changes' ? (
          <div className="rounded-xl border border-[var(--ink)]/15 bg-white p-3.5">
            <label
              htmlFor="slot-approval-changes-note"
              className="font-mono text-[10px] font-semibold uppercase tracking-[0.1em] text-[var(--plum)]"
            >
              {'↘'} <Trans>what to change</Trans>
            </label>
            <textarea
              id="slot-approval-changes-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              maxLength={1000}
              placeholder={t`shorter headline · less salesy · swap the image for a photo of the team · …`}
              className="field-input mt-2 w-full resize-none text-[13px]"
              disabled={pending}
            />
            <p className="mt-1.5 mag-voice text-[12px] leading-[1.45] text-[var(--ink)]/70">
              <Trans>
                “tell me what to change in plain words. i'll redraft + re-render before the publish
                window.”
              </Trans>
            </p>
          </div>
        ) : null}
      </div>

      <footer className="border-t border-[var(--line)] bg-white px-5 py-3">
        {mode === 'view' ? (
          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              disabled={pending}
              onClick={() => rejectMutation.mutate()}
              className="btn btn-sm btn-pulse disabled:cursor-not-allowed disabled:opacity-50"
            >
              {rejectMutation.isPending ? <Trans>rejecting…</Trans> : <Trans>reject</Trans>}
            </button>
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={pending}
                onClick={() => setMode('changes')}
                className="btn btn-sm btn-ghost disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Trans>request changes</Trans>
              </button>
              <button
                type="button"
                disabled={pending}
                onClick={() => approveMutation.mutate()}
                className="btn btn-sm btn-spell disabled:cursor-not-allowed disabled:opacity-50"
              >
                {approveMutation.isPending ? <Trans>approving…</Trans> : <Trans>approve</Trans>}
                {!approveMutation.isPending ? <span className="btn-arrow">→</span> : null}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              disabled={pending}
              onClick={() => setMode('view')}
              className="btn btn-sm btn-ghost disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Trans>back</Trans>
            </button>
            <button
              type="button"
              disabled={pending || note.trim().length === 0}
              onClick={() => requestChangesMutation.mutate()}
              className="btn btn-sm btn-spell disabled:cursor-not-allowed disabled:opacity-50"
            >
              {requestChangesMutation.isPending ? (
                <Trans>sending…</Trans>
              ) : (
                <Trans>send to mag</Trans>
              )}
              {!requestChangesMutation.isPending ? <span className="btn-arrow">→</span> : null}
            </button>
          </div>
        )}
      </footer>
    </Drawer>
  );
}
